/**
 * 중국어(간체) 사전. 앱(`apps/web`·`apps/mobile`)이 쓰는 `web` 가지만 있다 — `admin`·
 * `landing` 은 중국어로 전환할 길이 없어 옮기지 않았다(그 키는 `fallbackLng` 이 받는다).
 *
 * 키와 순서는 `en.ts` 의 `web` 과 같다. 키가 어긋나면 `parity.spec.ts` 가 red 다. 키에 붙은
 * 사연(왜 이 문구인가)은 `ko.ts`·`en.ts` 의 주석이 정본이고, 여기에는 중국어에만 해당하는
 * 것만 적는다.
 */
export const zh = {
  web: {
    common: {
      continue_: '继续',
      appName: 'MUZLY',
      back: '返回',
      next: '下一步',
      progress: '第 {{current}} 步，共 {{total}} 步',
    },
    offline: {
      offline: '当前处于离线状态',
      recovered: '网络已恢复',
    },
    link: {
      opening: '正在打开 MUZLY 应用',
      hint: '如果应用没有打开，可以在网页上继续。',
      continueWeb: '在网页上继续',
    },
    share: {
      title: '声乐诊断结果',
      loading: '正在加载结果',
      unavailableTitle: '无法查看这份结果',
      unavailableBody: '链接可能有误，或分享的结果已被删除。',
      cta: '我也来诊断一下',
    },
    metrics: {
      grade: {
        solid: '可靠',
        proxy: '近似',
        weak: '依据较弱',
      },
      sustain: {
        label: '气息支撑',
        note: '测量一口气能保持多久。分数是我们自己的量表，以 12 秒为满分，与参考区间用的不是同一把尺。',
      },
      pitchAccuracy: {
        label: '音准',
        note: '测量每个音离最近的半音偏了多少。并不是与固定旋律比对，所以只要音保持平稳就会得高分。',
      },
      pitchStability: {
        label: '音高稳定度',
        note: '测量音高晃动了多少。',
      },
      vibrato: {
        label: '颤音',
        note: '这是我们自己统计规律性音高波动的方法。目前还没有经过验证的基准，请仅作参考。',
      },
    },
    // 중국어는 영어처럼 문자 표기(`C5`)만 쓴다.
    note: {
      label: '{{note}}',
    },
    language: {
      label: '语言',
      ko: '한국어',
      en: 'English',
      zh: '中文',
    },
    tabs: {
      home: '首页',
      more: 'MY',
    },
    // 문서 본문은 중국어판이 없다 — 이 화면은 영어 문서를 영어 문구로 그린다(`policies.ts`).
    policy: {
      terms: { title: '服务条款' },
      privacy: { title: '隐私政策' },
      version: '生效日期',
      language: '语言',
      summaryTitle: '收集信息一览',
    },
    more: {
      title: 'MY',
      profile: '编辑个人资料',
      together: '相伴第 {{count}} 天',
      report: '我的声乐报告',
      privacy: '隐私政策',
      version: '应用版本',
      versionWeb: 'v{{web}}',
      versionNative: 'v{{app}} ({{build}}) · web v{{web}}',
      guest: '访客',
      stats: {
        runs: '诊断次数',
        runsUnit: '次',
        latest: '最近得分',
        latestUnit: '分',
        range: '我的音域',
        none: '—',
        noneLabel: '暂无',
        emptyHint: '完成第一次诊断后，这里会显示你的得分和音域。',
      },
      groups: {
        settings: '设置',
        support: '帮助与支持',
        legal: '法律信息',
        debug: '开发者选项',
      },
      items: {
        language: '语言',
        faq: '常见问题',
        contact: '联系我们',
        feedback: '提交反馈',
        deleteData: '删除全部诊断数据',
        terms: '服务条款',
        licenses: '开源许可',
        probe: '采集探针浮层',
        debugConsole: '引擎调试控制台',
        lockDebug: '锁定开发者选项',
      },
      deleteData: {
        title: '要删除全部诊断数据吗？',
        body: '这会删除保存在服务器上的诊断记录，并清空这台设备上的诊断历史。个人资料会保留。',
        confirm: '删除',
        cancel: '取消',
        done: '诊断数据已删除',
        partial: '部分数据未能删除，请重试',
        failed: '删除失败，请稍后再试',
      },
      language: {
        title: '语言',
        desc: '请选择应用使用的语言。',
        done: '完成',
        options: {
          ko: '韩语',
          en: '英语',
          zh: '中文',
        },
      },
      debugUnlocked: '开发者选项已开启',
      licenses: {
        intro: 'MUZLY 使用的开源软件。\n每组末尾附有完整的许可文本。',
        jump: '跳转到许可',
        // 중국어는 복수형 갈래가 하나라 접미사 없는 키 하나만 둔다.
        count: '{{count}} 个软件包',
        show: '查看 {{name}}',
        hide: '收起许可文本',
      },
      contactCta: '发送邮件',
      contactMail: {
        subject: '[MUZLY 咨询]',
        info: '请保留下面的信息，它能帮助我们排查问题。',
        os: '系统：{{os}}',
        failed: '无法打开邮件应用，请发送邮件至 {{email}}',
        failedCopied: '无法打开邮件应用，已将 {{email}} 复制到剪贴板',
      },
      docs: {
        faq: {
          title: '常见问题',
          items: {
            frequency: {
              q: '多久诊断一次比较好？',
              a: '每周一到两次，尽量在相近的时间和地点进行，这样更容易看出变化。',
            },
            variance: {
              q: '每次的结果都不一样。',
              a: '嗓子的状态、环境噪音和与麦克风的距离都会影响数值。请尽量在安静的房间里进行。',
            },
            score: {
              q: '综合得分代表什么？',
              a: '它是已测各项指标的平均值。各项之间的权重还没有校准，所以请和自己之前的结果比较，而不是和别人比较。',
            },
            mic: {
              q: '麦克风无法开启。',
              a: '请在设备设置中为 MUZLY 开启麦克风权限。回到应用后，可以从中断的那一步继续。',
            },
            storage: {
              q: '录音保存在哪里？',
              a: '诊断记录保存在你的设备上；分析结果和录音也会保存在我们的服务器上，方便你回放。',
            },
            delete: {
              q: '怎样删除我的数据？',
              a: '进入 MY → 删除全部诊断数据，即可删除保存在服务器上的诊断记录，并清空这台设备上的诊断历史。只想删除某一次诊断时，请点击该结果页面右上角的删除按钮。应用内无法处理的事项（例如删除个人资料），请发送邮件至 support@muzly.io。',
            },
          },
          notFound: '没有找到答案？',
          contact: '一对一咨询',
        },
        contact: {
          title: '联系我们',
          body: '请告诉我们哪里让你困惑或出了问题，我们会尽快回复。',
        },
      },
    },
    feedback: {
      title: '提交反馈',
      close: '关闭',
      prompt: {
        title: '到目前为止，\nMUZLY 用起来怎么样？',
        body: '喜欢的地方、不足的地方都很有帮助。\n我们会根据你的反馈\n把 MUZLY 做得更好。',
        cta: '提交反馈',
        later: '以后再说',
      },
      type: {
        title: '使用体验如何？',
        subtitle: '请选择最符合的一项。',
        good: { label: '很好', desc: '我很满意。' },
        okay: { label: '一般', desc: '整体还可以。' },
        bad: { label: '不太好', desc: '还需要改进。' },
      },
      detail: {
        title: '再多说一点吧！',
        subtitle: '好的地方、需要改进的地方，\n什么都可以。',
        label: '你的反馈',
        placeholder: '请在这里填写。',
        notes: {
          purpose: '你的反馈仅用于改进服务。',
          noReply: '我们不会逐一回复反馈。',
        },
      },
      submit: '提交',
      submitted: '反馈已发送',
      leave: {
        title: '不继续填写反馈了吗？',
        body: '要不发送就离开吗？\n随时可以从 MY 再回来。',
        stop: '离开',
        stay: '继续填写',
      },
    },
    home: {
      diagnosis: {
        section: '声乐诊断',
        subtitle: '{{count}} 步诊断，\n听听你的嗓音能做到什么',
        start: '开始诊断',
        resume: '继续诊断',
        duration: '约 {{value}} 分钟',
      },
      resume: {
        title: '有一次未完成的诊断',
        status: '进行到第 {{current}}/{{total}} 步',
        body: '可以从上次的步骤继续，\n也可以从头开始',
        restart: '重新开始',
        restartWarning: '重新开始会丢弃已测量的 {{current}} 个步骤',
        continue_: '继续',
        dismiss: '关闭',
      },
      recent: {
        section: '最近一次诊断',
        overall: '综合得分',
        score: '{{value}} / 100',
        uncalibrated: '综合得分是已测各项的未加权平均值（未校准）。',
        all: '查看全部结果',
      },
      tiles: {
        section: '最近结果',
        range: '{{low}}-{{high}}',
        hz: '{{value}}Hz',
        date: '{{year}}. {{month}}. {{day}}',
      },
      trend: {
        section: '近期声乐趋势',
        axisLabel: '趋势指标',
        axis: {
          overall: '综合得分',
          breath: '气息稳定度',
          range: '音域',
          pitchAccuracy: '音准',
        },
        axisShort: {
          overall: '综合',
          breath: '气息稳定度',
          range: '音域',
          pitchAccuracy: '音准',
        },
        semitones: '{{value}} 个半音',
        period: '最近 {{value}} 天',
        periodLabel: '趋势区间',
        outOf: '/ 100',
        deltaUp: '提高 {{value}}',
        deltaDown: '下降 {{value}}',
        deltaUpSemitones: '提高 {{value}} 个半音',
        deltaDownSemitones: '下降 {{value}} 个半音',
        empty: '在两个不同的日子完成诊断后，这里会显示趋势',
        alt: '最近 {{days}} 天的{{axis}} — 最后一次测量 {{date}}，{{value}}',
        altEmpty: '最近 {{days}} 天的{{axis}} — 没有测量记录',
      },
    },
    profile: {
      title: '编辑个人资料',
      groups: {
        basic: '基本信息',
        vocal: '声乐',
      },
      nickHelp: '请输入 {{min}}–{{max}} 个字符。',
      ageValue: '{{age}} 岁',
      note: '性别和年龄是音域与共鸣分析的基准。\n修改后从下一次诊断起生效。',
      unanswered: '尚未填写',
      save: '保存',
      unsavedConfirm: '有尚未保存的修改，离开后将会丢失。',
      saved: '已保存',
      fields: {
        gender: '性别',
        birthYear: '年龄',
        experience: '声乐经验',
        style: '想要的声乐风格',
      },
      photo: {
        label: '头像',
        alt: '你的头像',
        empty: '没有照片',
        change: '更换照片',
        add: '添加照片',
        camera: '拍照',
        library: '从相册选择',
        remove: '移除照片',
        cancel: '取消',
        localOnly: '照片只保存在这台设备上。',
        denied: '请在设置中开启照片访问权限。',
        failed: '无法加载这张照片，请重试。',
      },
    },
    brand: {
      mascotAlt: 'MUZLY 吉祥物',
    },
    onboarding: {
      other: {
        placeholder: '请输入你的回答',
        label: '其他 — 请输入你的回答',
      },
      nick: {
        title: '请设置在 MUZLY 中使用的昵称和语言',
        subtitle: '昵称可以随时在设置中修改。',
        label: '昵称',
        placeholder: '请输入名字',
        hint: '最多 {{max}} 个字符',
        fallback: '朋友',
      },
      gender: {
        title: '先来确定你嗓音的基准',
        subtitle: '音域和共鸣的基准因性别而异。',
        options: {
          female: '女',
          male: '男',
          none: '不愿透露',
        },
      },
      birthYear: {
        title: '{{nick}}，请告诉我们你的年龄。',
        subtitle: '这是准确分析所需要的。',
        placeholder: '选择年份',
        option: '{{value}}（{{age}} 岁）',
      },
      experience: {
        title: '{{nick}}，你唱歌到什么程度了？',
        options: {
          first: '这是第一次',
          hobby: '只是爱好',
          lesson: '上过课',
          pro: '专业水平',
        },
      },
      style: {
        title: '你想要什么样的嗓音？',
        subtitle: '选择你向往的风格，我们会以它为参照进行比较。',
        options: {
          powerful: '有力量的唱功',
          ballad: '抒情慢歌',
          pop: '明亮的流行',
          classic: '古典 · 音乐剧',
          jazz: '爵士 · 灵魂乐',
        },
      },
      purpose: {
        title: '{{nick}}，今天是为什么而来？',
        options: {
          curious: '只是好奇',
          check: '想了解自己的水平',
          track: '想记录持续的进步',
          audition: '准备试镜或考试',
        },
      },
      environment: {
        title: '你现在所在的地方安静吗？',
        subtitle: '环境噪音会影响分析结果。',
        options: {
          quiet: '安静的室内',
          noisy: '嘈杂的室内',
          outdoor: '室外',
        },
      },
      condition: {
        title: '今天嗓子感觉怎么样？',
        options: {
          best: '非常好',
          normal: '一般',
          husky: '有点沙哑',
          bad: '不太好',
        },
      },
      surveyDone: {
        title: '声乐设置\n已完成！',
        banner: '现在来听听你的声音吧？',
      },
    },
    diagnosis: {
      close: '离开诊断',
      intro: {
        testsTitle: '声乐诊断包含 {{value}} 项嗓音测试',
        durationTitle: '声乐诊断大约需要 {{value}} 分钟',
        durationSubtitle: '不需要唱得多好\n放松地发出声音就行',
        slides: '第 {{current}} / {{total}} 页',
        prev: '上一页',
        next: '下一页',
        cta: '开始诊断',
      },
      guide: {
        preview: '试听旋律',
        previewStop: '停止试听',
        previewTone: '听基准音',
        previewToneStop: '停止基准音',
        earphones: '请戴上耳机 — 伴奏不会混进你的声音里，结果会更准确',
        step: '{{current}} / {{total}}',
        duration: '用时：{{value}}',
        unlimited: '不限时',
        seconds: '{{value}} 秒',
        start: '开始测试',
      },
      tests: {
        sustain: {
          title: '基础发声',
          summary: '对准基准音 A，持续发“啊”',
          desc: '先听基准音 A，然后在任意舒服的八度上用“啊”保持这个音，能坚持多久就坚持多久。',
        },
        low: {
          title: '低音域',
          summary: '在舒服的范围内尽量唱低',
          desc: '慢慢滑向你能发出的最低音。',
        },
        high: {
          title: '高音域',
          summary: '在舒服的范围内尽量唱高',
          desc: '不要勉强，轻松地唱到你的最高音。',
        },
        song: {
          title: '跟唱',
          summary: '跟着播放的旋律唱一遍',
          desc: '会播放一段简短的旋律。请按相同的音高和节奏跟唱 — 用“啊”哼唱也可以。',
        },
      },
      segments: {
        sustain: {
          title: '对准基准音 A，尽可能长地发“啊”。',
          subtitle: '放松地拉长声音，声音断了就点“完成”。',
          upNext: '现在在基准音 A 上长长地发“啊”',
        },
        low: {
          title: '用“啊”滑到你能唱出的最低音。',
          subtitle: '一个音一个音地往下。唱不下去了就点“完成”。',
          upNext: '现在挑战你的最低音',
        },
        high: {
          title: '现在唱到你能舒服发出的最高音。',
          subtitle: '嗓子疼就停下来，不必勉强。',
          upNext: '现在挑战你的最高音',
        },
        song: {
          title: '跟着听到的旋律唱一遍。',
          subtitle: '音高和节奏都要对上。漏掉一部分也没关系。',
          upNext: '现在跟唱这段旋律',
        },
      },
      track: {
        label: '保持基准音时逐渐填满的时间轴',
        traceLabel: '随时间变化的音高',
        gaugeLabel: '你正在唱的音有多高',
        vowel: '啊',
        sharpBy: '+{{value}} 音分',
        flatBy: '−{{value}} 音分',
        currentNote: '当前音',
        tick: {
          high: '高',
          mid: '中',
          low: '低',
        },
      },
      countdown: {
        announce: '{{value}} 秒后开始',
      },
      noisyRoom: {
        title: '环境噪音太大，\n这次测量不够可靠。',
        banner: '换个安静的地方再试一次，结果会更准确！',
        retry: '重新测量',
      },
      record: {
        start: '开始录音',
        finish: '结束录音',
        finishLabel: '完成',
        noVoiceYet: '还没有听到你的声音',
        replayDisabled: '录音时无法重播旋律',
        recording: '录音中',
        elapsed: '{{value}} 秒',
        remaining: '还剩 {{value}} 秒',
        elapsedLabel: '已用时间',
        listening: {
          idle: '舒服地保持，能多久就多久',
          hearing: '听得很清楚',
          faint: '声音有点小 — 请再唱大声一点。',
          silent: '听不到声音 — 试着放松地发声。',
        },
        measuring: '测量中',
        waveformLabel: '正在听你的声音',
        backingLabel: '伴奏播放位置',
        pause: '暂停',
        resume: '继续',
        restart: '重新开始',
        interrupted: '录音中断了',
        interruptedHint: '这次录音已经和伴奏对不上了。我们从头再唱一遍吧。',
      },
      bound: {
        low: '最低 $t(web.note.label)',
        high: '最高 $t(web.note.label)',
      },
      attempt: {
        noSound: '没有听到任何声音',
        noRange: '没有听出音高，这次没能测出音域',
        sustain: '发声 {{value}} 秒',
        unstable: '音高有些晃动，不利于分析。试着稳稳地保持一个音。',
      },
      pitchGraph: {
        label: '实时音高',
      },
      song: {
        loading: '正在准备旋律',
        loadFailed:
          '旋律加载失败。请重试，或只跳过这一项测试 — 仍然可以得到结果。',
        retry: '重试',
        skip: '跳过这项测试',
        result: '音准 {{accuracy}}% · {{total}} 个音中唱了 {{sung}} 个',
        unmeasured: '没有听到可以比对的声音',
        unmeasuredHint: '这次录音不会产生音准得分',
        headphoneAdvice: {
          title: '请戴上耳机演唱',
          body: '伴奏从扬声器播放时，麦克风会同时收到伴奏，音准测得不够准确。\n有线或蓝牙耳机都可以。',
          confirm: '知道了',
        },
        tendency: {
          low: {
            flat: '低音部分大多偏低',
            sharp: '低音部分大多偏高',
          },
          high: {
            flat: '高音部分大多偏低',
            sharp: '高音部分大多偏高',
          },
        },
      },
      review: {
        heading: '本次测量',
        testDone: '<{{test}}> 已完成',
        nextTestHint: '接着进行下一项吧！',
        lastTestHint: '现在来分析你的结果吧！',
        attempts: '共尝试 {{value}} 次 — 保留最好的一次',
        retry: '重做',
        confirm: '确定',
      },
      stepDone: {
        title: '第 {{step}} 步 {{test}}\n已完成',
        retry: '重新测量',
        next: '下一步',
        warnHint: '可以继续，但重新测量会更准确',
        finishing: '正在收尾…',
      },
      allDone: {
        title: '所有测试都完成了！',
        body: '辛苦了。\n我们根据你刚才的演唱\n生成了专属结果！',
        result: '查看结果',
      },
      done: {
        title: '已完成 {{value}} 项测试',
        result: '查看结果',
        retry: '重新录音',
      },
      error: {
        title: {
          denied: '无法使用麦克风',
          blocked: '无法使用麦克风',
          invalid: '这次录音没有保存',
          unsupported: '现在无法开始录音',
        },
        denied: '麦克风不可用，无法开始测量。',
        blocked: '麦克风在设置中被关闭了。开启后可以从这里继续。',
        unsupported: '这台设备不支持录音。',
        invalid: '基础发声中的“啊”不够平稳。',
        keptNote: '已经测量的步骤仍然保留着。',
        invalidNote: '其他步骤会保留，只需重做基础发声。',
        openSettings: '打开设置',
        retrySustain: '重做基础发声',
        restartAll: '重新开始',
        mockInvalid:
          '在浏览器中，我们分析的是合成声音而不是你的麦克风 — 这不是真实测量，所以分析无法完成。请在应用中进行真正的诊断。',
        retry: '重新录音',
      },
    },
    history: {
      title: '历史结果',
      intro: '每次诊断都会与上一次进行比较。',
      emptyTitle: '还没有保存的诊断',
      emptyPeriod: '这段时间内没有诊断',
      score: '{{value}} 分',
      emptyBody: '完成诊断后，记录会累积在这里。',
      restart: '开始新的诊断',
      date: '{{month}}/{{day}}',
      delta: {
        sec: '{{value}} 秒',
        point: '{{value}} 分',
      },
    },
    result: {
      title: '你的声乐结果',
      share: {
        label: '分享结果',
        confirmTitle: '要分享这份结果吗？',
        confirmBody: '任何拿到链接的人都可以看到这份诊断结果。',
        confirm: '分享',
        cancel: '取消',
        text: '我的 MUZLY 声乐诊断：{{score}} 分！你也来诊断一下自己的嗓音吧',
        textNoScore: '来看看我的 MUZLY 声乐诊断！你也来诊断一下自己的嗓音吧',
        copied: '链接已复制',
        failed: '分享失败，请稍后再试',
      },
      detail: {
        title: '你的结果',
      },
      delete: {
        label: '删除这次诊断',
        title: '要删除这次诊断吗？',
        body: '这会删除保存在服务器上的这次诊断记录，并将它从这台设备的历史中移除。',
        confirm: '删除',
        cancel: '取消',
        done: '诊断已删除',
        failed: '删除失败，请稍后再试',
      },
      subtitle: '我们只展示从你的录音中实际测量到的内容。',
      fatigue: {
        radarLabel: '声带疲劳雷达图，四种状态中已测量 {{filled}} 种',
        section: '声带疲劳',
        note: '你的得分随自述状态的变化。状态好与不好的日子差距越大，说明嗓音越受状态影响。',
        badge: '{{score}} · ×{{takes}}',
        empty: '暂无',
        axis: {
          best: {
            label: '最佳状态',
            note: '感觉状态最好的日子的平均值',
            prompt: '在状态好的日子录一次，就会填上这里',
          },
          normal: {
            label: '平常',
            note: '平常日子的平均值',
            prompt: '在平常的日子录一次，就会填上这里',
          },
          husky: {
            label: '略微沙哑',
            note: '嗓子有点紧的日子的平均值',
            prompt: '在略微沙哑时录一次，就会填上这里',
          },
          bad: {
            label: '状态不佳',
            note: '嗓子不舒服的日子的平均值',
            prompt: '在状态不佳的日子录一次，就会填上这里',
          },
        },
      },
      pending: '这次没有测到',
      uncalibrated: '未校准的量表',
      score: '{{value}} / 100',
      mock: '在浏览器中，我们分析的是合成声音而不是你的麦克风。这份结果不会保存。',
      failed: {
        title: '无法分析这次录音',
        body: '可用的声音不够。请重新录音。',
        retry: '重新录音',
      },
      env: {
        noisy: '测试时周围比较嘈杂 — 这份结果请仅作大致参考',
      },
      condition: {
        best: '你说今天嗓子状态很好 — 这接近你目前的最佳水平。',
        normal: '你说今天嗓子状态一般。',
        husky: '你说嗓子有点沙哑 — 清晰度和高音可能比平时偏低。',
        bad: '你说今天嗓子状态不好 — 状态好的时候数值可能会不一样。',
      },
      sustain: {
        section: '测试 1. 持续发“啊”',
        breath: '气息支撑',
        seconds: '{{value}} 秒',
        band: {
          short: '比同年龄同性别的平均值 15–20 秒略短',
          inBand: '处于同年龄同性别的平均值 15–20 秒之内',
          long: '比同年龄同性别的平均值 15–20 秒更长',
        },
        stability: '稳定度',
        clarity: '清晰度',
        clarityLevel: { low: '气声较多', mid: '一般', high: '清晰' },
        clarityNote: '音色中的气息噪音越少，嗓音听起来越清晰。',
        resonance: '共鸣',
        resonanceLevel: { low: '平淡', mid: '一般', high: '丰富' },
        resonanceNote: '带一点共鸣，声音能传得更远。',
      },
      tabs: {
        label: '指标详情',
        footnote: '结果会随环境和当天的状态而变化。',
        name: {
          fatigue: '声带疲劳',
          range: '音域',
          registers: '音准',
          vibrato: '颤音',
          breath: '气息',
          formant: '歌手共振峰',
          resonance: '共鸣形态',
        },
        subtitle: {
          fatigue: '你的得分随自述状态的变化。',
          range: '你能发出的音的范围',
          registers: '各个声区的音高保持得有多平稳。',
          vibrato: '音高的规律性波动 — 速度、幅度、规律性。',
          breath: '保持一个音时的晃动。',
          formant: '嗓音的共鸣（亮泽）形成得如何。',
          resonance: '共鸣偏亮还是偏暗，分散还是集中。',
        },
        level: { high: '良好', mid: '一般', low: '留意' },
        insightTitle: '一句话总结',
        keyPoints: '要点',
        details: '分项',
        legend: { mine: '我的结果', average: '平均' },
        insight: {
          registers: {
            lowExtension: '这次最低的一段晃动最大。慢慢往下走，把重量放掉。',
            modal: '这次说话音区晃动最大。从舒服的音开始重新打基础。',
            passaggio: '从中音到高音的过渡晃动最大。这个位置对谁都不容易。',
            head: '这次高音段晃动最大。与其硬往上顶，不如先扩大轻松的范围。',
            highExtension:
              '这次最高的一段晃动最大。这已经是极限边缘，慢慢拓展就好。',
          },
          vibrato: {
            inBand: '你的颤音速度和幅度都很自然。',
            slow: '你的颤音偏慢，音色听起来可能偏沉。',
            fast: '你的颤音偏快，音色听起来可能偏急。',
            narrow: '你的颤音幅度偏窄 — 音高几乎不动，不太听得出是颤音。',
            wide: '你的颤音幅度偏宽 — 摆动太大会让人听不清你在唱哪个音。',
          },
          breath: {
            start: '开头的晃动最大。开始前先吸足一口气。',
            middle: '中段气息变弱，晃动变大了。',
            late: '结尾的晃动最大 — 那是气用完的地方。',
          },
          formant: {
            inBand: '你的共鸣落在有效频段之内。',
            centerLow: '你的共鸣聚在有效频段之下，听起来可能偏厚。',
            centerHigh: '你的共鸣聚在有效频段之上，听起来可能偏薄。',
            levelLow: '你的共鸣比较弱。频段是对的，但那里的能量不多。',
          },
          resonance: {
            concentrated:
              '你的共鸣非常集中。稍微打开一些，声音会更饱满、更稳定。',
            wide: '你的共鸣分布较宽。饱满，但可能不够集中。',
            dark: '你的共鸣偏暗 — 厚实而柔和。',
            bright: '你的共鸣偏亮 — 清晰，但可能听起来偏薄。',
            balanced: '明亮度和分散度都接近中间。',
          },
        },
      },
      registers: {
        intro: '把你的音域分成五段',
        legendStability: '稳定度 (0-100)',
        legendHistory: '与历史相比 (%)',
        desc: {
          lowExtension: '接近气泡音的音区',
          modal: '主要音区',
          passaggio: '最费力的位置',
          head: '高音',
          highExtension: '超出常用音区',
        },
        spanShort: '{{low}}-{{high}} · {{desc}}',
        section: '音准 — 各声区的稳定度',
        accuracy: '音准',
        note: '把你的音域分成五段，分别按声音的平稳程度评分。这次没有唱到的段落留空。',
        span: '{{low}} - {{high}} · 与上次相比 {{history}}',
        history: '{{value}}%',
        historyNone: '—',
        name: {
          lowExtension: '低音延伸区',
          modal: '自然声区',
          passaggio: '换声区',
          head: '头声区',
          highExtension: '高音延伸区',
        },
      },
      breath: {
        legendScore: '气息稳定度得分',
        legendAverage: '平均基准',
        bySegment: '各段稳定度',
        point: '{{value}} 分',
        desc: {
          high: '保持得很稳。',
          mid: '有一点晃动。',
          low: '晃动变大了。',
        },
        badge: { high: '稳定', mid: '一般', low: '留意' },
        chartLabel: '各段气息稳定度',
        axisSec: '{{value}} 秒',
        axisScore: '纵轴 {{min}}–{{max}}',
        section: '气息稳定度',
        row: '{{name}}（{{from}}-{{to}} 秒）',
        name: { start: '开头', middle: '中段', late: '结尾' },
        note: '一口气之内晃动出现在哪里。录音没有到达的段落留空。',
      },
      formant: {
        tickHz: '{{value}}k',
        unitHz: 'Hz',
        unitDb: 'dB',
        section: '歌手共振峰',
        note: '你的共鸣聚集在哪个频段，以及在那里有多强。',
        center: '共鸣中心',
        centerValue: '{{value, number}} Hz',
        centerRange: '有效频段 {{min}}-{{max}} kHz',
        level: '共鸣强度',
        levelValue: '{{value}} dB',
        levelRange: '有效频段 {{min}} 至 {{max}} dB',
      },
      resonance: {
        section: '共鸣形态',
        note: '你的共鸣偏亮还是偏暗，是分散还是集中。',
        brightness: '明亮度',
        // 화면이 ` ↔ ` 로 잘라 양 끝 라벨로 쓴다 — 토큰을 그대로 둔다.
        brightnessAxis: '暗 ↔ 亮',
        spread: '分散度',
        spreadAxis: '宽 ↔ 集中',
        axisValue: '{{axis}} · {{value, number}} Hz',
        brightnessLevel: {
          veryLow: '偏暗',
          low: '略暗',
          mid: '均衡',
          high: '略亮',
          veryHigh: '偏亮',
        },
        spreadLevel: {
          veryLow: '偏宽',
          low: '略宽',
          mid: '均衡',
          high: '略集中',
          veryHigh: '偏集中',
        },
        style: '共鸣风格',
        styleNote: '你的共鸣目前所在的位置。',
        here: '当前位置',
        axis: {
          top: '明亮清晰',
          bottom: '深暗饱满',
          left: '宽 ←',
          right: '→ 集中',
        },
      },
      vibrato: {
        axisSecShort: '{{value}} 秒',
        depthRef: '参考 ±{{min}}-{{max}} 音分',
        centsUnit: '音分',
        rateRef: '参考 {{min}}-{{max}} Hz',
        depthRatio: '约为半音的 {{ratio}} 倍',
        regularityBasis: '依据周期波动',
        regularityScale: { low: '低', mid: '一般', high: '良好' },
        explain: {
          title: '如何看幅度',
          body: '颤音幅度（音分）是音高上下摆动的距离。',
          small: { title: '偏浅', desc: '细微而平淡的波动' },
          good: { title: '刚刚好', desc: '自然而饱满' },
          large: { title: '过深', desc: '幅度大而不稳的晃动' },
        },
        chartLabel: '颤音示意图 — {{rate}}Hz，幅度 {{depth}} 音分',
        axisSec: '{{value}} 秒',
        axisCents: '±{{value}} 音分',
        waveNote: '这是根据测得的速度和幅度画出的示意图，不是录音的波形。',
        chartLabelMeasured: '你这次的颤音 — {{rate}}Hz，幅度 {{depth}} 音分',
        waveNoteMeasured:
          '这是你这次音高的实际波动（3 秒），去掉了缓慢的漂移，并把幅度对齐到测量值。',
        section: '颤音',
        rate: '速度',
        rateValue: '{{value}} Hz',
        rateNote: '自然的颤音通常在 {{min}}–{{max}} Hz 左右。',
        depth: '幅度',
        depthValue: '±{{value}} 音分',
        depthNote: '摆动了半音的 {{ratio}} 倍。',
        regularity: '规律性',
        regularityValue: '{{value}} %',
        regularityNote: '颤音每个周期有多均匀。',
      },
      range: {
        wholeTitle: '我的完整音域',
        wholeDesc: '你的嗓音目前能发出的最低音和最高音',
        lowest: '最低音',
        highest: '最高音',
        widest: '最宽跨度',
        hz: '({{value}} Hz)',
        semitones: '({{value}} 个半音)',
        section: '测试 2. 低音与高音',
        legendFilled: '能唱到的音',
        legendEmpty: '唱不到的音',
        mine: '你的音域',
        notes: '{{low}} ~ {{high}}',
        span: {
          whole: '约 {{octaves}} 个八度',
          half: '约 {{octaves}}.5 个八度',
          halfOnly: '约半个八度',
        },
        voiceType: '可能的声部',
        types: {
          bass: '接近男低音',
          baritone: '接近男中音',
          tenor: '接近男高音',
          contralto: '接近女低音',
          mezzo: '接近女中音',
          soprano: '接近女高音',
        },
        // 받침에 따라 갈리는 것은 한국어 조사뿐이다 — 두 키가 같은 문장이다.
        similar: {
          gwa: '与 {{singers}} 相近（推测）',
          wa: '与 {{singers}} 相近（推测）',
        },
        // 중화권에서 통용되는 한자 표기. 표기가 굳지 않은 두 사람(최백호·소향)은 로마자로 둔다.
        singers: {
          choiBaekho: 'Choi Baek-ho',
          kimKwangseok: '金光石',
          kimDongryul: '金东律',
          sungSikyung: '成始璄',
          parkHyoshin: '朴孝信',
          kimBumsoo: '金范秀',
          leeSora: '李素罗',
          jangJaein: '张才人',
          iu: 'IU',
          taeyeon: '泰妍',
          parkJunghyun: '朴正炫',
          sohyang: 'Sohyang',
        },
        register: '换声',
        registerTimes: '{{count}} 次',
        registerNone: '往上唱时没有破音',
        registerCount: '往上唱时破音了 {{count}} 次',
        registerPending: '唱得不够高，无法判断',
      },
      replay: {
        title: '听听我的录音',
        play: '播放{{take}}',
        stop: '停止{{take}}',
        failed: '无法播放录音，请稍后再试。',
      },
      song: {
        section: '测试 3. 跟唱',
        accuracy: '跟唱音准',
        percent: '{{value}}%',
        hits: '{{total}} 个音中约有 {{hit}} 个唱准了',
        segments: '分段',
        segment: {
          low: {
            onPitch: '低音部分很准',
            flat: '低音部分略微偏低',
            sharp: '低音部分略微偏高',
          },
          high: {
            onPitch: '高音部分也很准',
            flat: '高音部分略微偏低',
            sharp: '高音部分略微偏高',
          },
          pending: '唱的音不够，无法按段判断',
        },
        timing: '节奏',
        timingSteady: '你跟上了节拍',
        timingLate: '你进入得略微偏晚',
        timingEarly: '你进入得略微偏早',
        timingPending: '对上的音不够，无法判断节奏',
        skipped: '这次没有进行跟唱测试',
      },
      summary: {
        section: '综合',
        total: '总分',
        uncalibrated: '总分是已测各项的简单平均值（权重未校准）',
        axes: '总分的构成',
        type: '声乐类型',
        // 조각은 그 자체로 서는 말이다 — 한국어처럼 앞 조각이 뒤 조각을 꾸미지 않는다.
        type1: '{{p1}}',
        type2: '{{p1}}、{{p2}}',
        type3: '{{p1}}、{{p2}}、{{p3}}',
        typeParts: {
          tone: { low: '带气声', mid: '柔和', high: '清亮' },
          stability: { low: '不太稳', mid: '平稳', high: '稳定' },
          shape: {
            narrow: '集中型',
            balanced: '均衡型',
            wide: '宽音域型',
            unknown: '嗓音',
          },
        },
        strengths: '优势',
        improvements: '待提升',
        points: {
          sustain: {
            strong: '一口气能保持很久',
            weak: '气息更长的话，乐句会更从容',
          },
          stability: {
            strong: '音色平稳，没有晃动',
            weak: '保持一个音时音高有些漂移',
          },
          clarity: {
            strong: '音色干净，气息噪音少',
            weak: '气息噪音有时会让音色变模糊',
          },
          resonance: {
            strong: '音色有共鸣，穿透力好',
            weak: '共鸣再多一些，声音能传得更远',
          },
          range: {
            strong: '可用音域很宽',
            weak: '拓宽音域后能唱的歌会更多',
          },
          register: {
            strong: '进入高音时不会破音',
            weak: '在换声的位置会破音',
          },
          songAccuracy: {
            strong: '很好地跟上了参考旋律',
            weak: '有些乐句偏离了旋律',
          },
          timing: {
            strong: '进入的时机很准',
            weak: '起音的时机有些偏差',
          },
        },
      },
      cta: '返回首页',
    },
  },
};
